/**
 * Named gesture-tuning presets.
 *
 * These were previously magic numbers scattered across three files with subtly
 * different values (deadzone 8, rubber-band 0.25 vs 0.3, fling 300/50/2 vs
 * 300/40/2…). Centralising them keeps the feel consistent and makes tuning a
 * one-line change. Each preset bundles the recognizer's `fling`/`thresholdPx`
 * with the consumer-facing layout constants (commit thresholds, rubber-band
 * limits, animation durations) that the recognizer itself does not own.
 */

import type { FlingConfig } from "./types";

const GESTURE_DEADZONE_PX = 8;

const STANDARD_FLING: FlingConfig = {
  maxDurationMs: 300,
  minDistancePx: 50,
  axisRatio: 2,
};

/** Per-row swipe-to-action (complete / delete). */
export const TODO_ROW_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 100,
  rubberBandLimitPx: 130,
  rubberBandFactor: 0.25,
  hapticMs: 15,
  commitAnimationMs: 300,
  cancelAnimationMs: 240,
  fallbackWidthPx: 400,
} as const;

/** Page-level swipe between lists (X) and pull-to-dashboard (Y). */
export const PAGE_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  indicatorTriggerPx: 10,
  commitThresholdPx: 80,
  rubberBandFactor: 0.3,
  transitionMs: 240,
  fallbackWidthPx: 320,
  fallbackHeightPx: 800,
  fling: { ...STANDARD_FLING },
  /** Vertical fling needs less travel than horizontal. */
  flingMinYDistancePx: 15,
} as const;

/** Month calendar: horizontal swipe to page between months (carousel slide). */
export const CALENDAR_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  /** Commit at the smaller of a fixed travel or a fraction of the grid width. */
  commitThresholdPx: 56,
  commitThresholdRatio: 0.22,
  transitionMs: 240,
  fallbackWidthPx: 300,
  hapticMs: 12,
  fling: { ...STANDARD_FLING },
} as const;

/**
 * Mobile navigation drawer: drag it toward its own edge to dismiss it.
 *
 * The drawer is anchored to the left, so only a leftward drag means anything.
 * Dragging the other way has nothing to reveal — it gets a short, capped
 * rubber-band so the panel acknowledges the finger without ever detaching from
 * its edge or committing anything.
 */
export const NAV_DRAWER_DISMISS = {
  thresholdPx: GESTURE_DEADZONE_PX,
  /** Travel toward the edge that commits the close on release. */
  commitThresholdPx: 72,
  /** Give on the closed-direction drag (the one that does nothing). */
  rubberBandFactor: 0.25,
  /** Hard cap on that give. */
  rubberBandLimitPx: 24,
  hapticMs: 12,
  /** Used for the backdrop fade when the panel has not been measured yet. */
  fallbackWidthPx: 280,
  /** A flick dismisses without reaching `commitThresholdPx`. */
  fling: { maxDurationMs: 300, minDistancePx: 40, axisRatio: 2 } satisfies FlingConfig,
} as const;

/** Todo-detail drawer carousel (X between todos) and pull-to-close (Y). */
export const DRAWER_CAROUSEL = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 80,
  commitThresholdRatio: 0.22,
  rubberBandFactor: 0.3,
  transitionMs: 240,
  fallbackWidthPx: 320,
  fallbackHeightPx: 900,
  verticalCommitThresholdPx: 220,
  indicatorTriggerPx: 5,
  horizontalIndicatorTriggerPx: 10,
  fling: { maxDurationMs: 300, minDistancePx: 40, axisRatio: 2 } satisfies FlingConfig,
} as const;
